import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/app/store/auth';
import { User, Edit, Key } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SimpleEditProfileDialog } from '../components/SimpleEditProfileDialog';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar';

export function SimpleProfilePage() {
    const { t } = useTranslation();
    const user = useUser();
    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    if (!user) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 max-w-4xl">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold tracking-tight">{t('profile.settings')}</h1>
                <p className="text-sm text-muted-foreground">
                    {t('profile.settingsDesc')}
                </p>
            </div>

            {/* Profile Information */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <User className="h-4 w-4" />
                        {t('profile.profileInfo')}
                    </CardTitle>
                    <CardDescription className="text-sm">
                        {t('profile.profileInfoDesc')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                    <div className="space-y-2 pb-2">
                        <Label className="text-sm">{t('profile.profilePicture')}</Label>
                        <div className="flex items-center gap-3">
                            <EnhancedAvatar
                                name={user.name}
                                avatarUrl={user.avatarUrl}
                                avatarColor={user.avatarColor}
                                userId={user.id}
                                size="lg"
                            />
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="name" className="text-sm">{t('profile.name')}</Label>
                            <Input
                                id="name"
                                value={user.name}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-sm">{t('profile.email')}</Label>
                            <Input
                                id="email"
                                type="email"
                                value={user.email}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.status')}</Label>
                            <div className="flex items-center gap-2">
                                <Badge variant={user.isActive ? "success" : "danger"} className="text-xs">
                                    {user.isActive ? t('profile.active') : t('profile.inactive')}
                                </Badge>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.roles')}</Label>
                            <div className="flex items-center gap-2 flex-wrap">
                                {user.roles?.map((role) => (
                                    <Badge key={role.id} variant="info" className="text-xs">
                                        {role.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-sm">{t('profile.multiFactorAuth')}</Label>
                        <div className="flex items-center gap-2">
                            <Badge variant={user.mfa?.totpEnabled ? "success" : "danger"} className="text-xs">
                                {user.mfa?.totpEnabled ? t('profile.totpEnabled') : t('profile.totpDisabled')}
                            </Badge>
                            <Badge variant={user.mfa?.webauthnEnabled ? "success" : "danger"} className="text-xs">
                                {user.mfa?.webauthnEnabled ? t('profile.webauthnEnabled') : t('profile.webauthnDisabled')}
                            </Badge>
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.created')}</Label>
                            <Input
                                value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : t('profile.unknown')}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.lastUpdated')}</Label>
                            <Input
                                value={user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : t('profile.unknown')}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-2">
                <Button
                    variant="outline"
                    onClick={() => setEditProfileOpen(true)}
                >
                    <Edit className="h-4 w-4 mr-2" />
                    {t('profile.editProfile')}
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setChangePasswordOpen(true)}
                >
                    <Key className="h-4 w-4 mr-2" />
                    {t('profile.changePassword')}
                </Button>
            </div>

            {/* Modals */}
            <SimpleEditProfileDialog
                open={editProfileOpen}
                onOpenChange={setEditProfileOpen}
            />
            <ChangePasswordDialog
                open={changePasswordOpen}
                onOpenChange={setChangePasswordOpen}
            />
        </div>
    );
}
